import { create } from 'zustand';

export type WidgetSize = 'small' | 'medium' | 'large';

export interface WidgetInstance {
  instanceId: string;
  widgetId: string;
  size: WidgetSize;
  /** Per-widget chart mark/series color overrides, indexed by the rendered datum/series. */
  chartColors?: string[];
}

export interface Dashboard {
  id: string;
  name: string;
  icon?: string;
  href: string;
  builtIn: boolean;
  widgets: WidgetInstance[];
  // Set for shared org-level dashboards loaded into transientDashboards;
  // used by the canvas to gate Customize to the original creator.
  createdBy?: string | null;
}

interface PersistedState {
  dashboards: Record<string, Dashboard>;
  order: string[];
}

interface DashboardStore extends PersistedState {
  userId: string | undefined;
  initialize: (userId: string | undefined) => void;

  /**
   * Server-loaded shared dashboards live here while the user is viewing
   * them. They are NOT persisted to localStorage — the server is the
   * source of truth. Mutations against a transient dashboard stay in
   * memory until the user navigates away.
   */
  transientDashboards: Record<string, Dashboard>;
  setTransientDashboard: (dashboard: Dashboard) => void;
  clearTransientDashboard: (id: string) => void;

  getDashboard: (id: string) => Dashboard | undefined;
  createDashboard: (name: string, opts?: { icon?: string }) => string;
  deleteDashboard: (id: string) => void;
  renameDashboard: (id: string, name: string) => void;

  addWidget: (dashboardId: string, widgetId: string, size?: WidgetSize) => string | null;
  removeWidget: (dashboardId: string, instanceId: string) => void;
  setWidgetSize: (dashboardId: string, instanceId: string, size: WidgetSize) => void;
  setWidgetChartColor: (instanceId: string, index: number, color: string) => void;
  clearWidgetChartColor: (instanceId: string, index: number) => void;
  reorderWidgets: (dashboardId: string, instanceIds: string[]) => void;
  resetDashboard: (dashboardId: string) => void;
}

const BUILTIN_DASHBOARDS: Record<string, Dashboard> = {
  main: {
    id: 'main',
    name: 'Main Dashboard',
    icon: 'layout-dashboard',
    href: '/dashboard',
    builtIn: true,
    widgets: [
      { instanceId: 'main-kpi-active-loads', widgetId: 'kpi-active-loads', size: 'medium' },
      {
        instanceId: 'main-kpi-driver-utilization',
        widgetId: 'kpi-driver-utilization',
        size: 'medium',
      },
      { instanceId: 'main-kpi-on-time-delivery', widgetId: 'kpi-on-time-delivery', size: 'medium' },
      { instanceId: 'main-kpi-avg-rate-mile', widgetId: 'kpi-avg-rate-mile', size: 'medium' },
      { instanceId: 'main-kpi-total-revenue', widgetId: 'kpi-total-revenue', size: 'medium' },
      { instanceId: 'main-kpi-open-invoices', widgetId: 'kpi-open-invoices', size: 'medium' },
      {
        instanceId: 'main-kpi-pending-settlements',
        widgetId: 'kpi-pending-settlements',
        size: 'medium',
      },
      // ATL-001469 — NetSuite tiles. Only drawn while the `netsuite` feature
      // is on and NetSuite is connected (useWidgetAvailability).
      {
        instanceId: 'main-kpi-netsuite-pending-invoices',
        widgetId: 'kpi-netsuite-pending-invoices',
        size: 'medium',
      },
      {
        instanceId: 'main-kpi-netsuite-pending-settlements',
        widgetId: 'kpi-netsuite-pending-settlements',
        size: 'medium',
      },
      { instanceId: 'main-kpi-delivered', widgetId: 'kpi-delivered', size: 'medium' },
      {
        instanceId: 'main-completion-progress',
        widgetId: 'panel-completion-progress',
        size: 'medium',
      },
      {
        // ATL-001470 — Missing Documents per-module counts. Full-width slim
        // band under the KPI strip (span 12) so the chart rows below keep
        // their original 6+6 pairing.
        instanceId: 'main-missing-documents',
        widgetId: 'panel-missing-documents',
        size: 'large',
      },
      {
        instanceId: 'main-panel-load-creation-sources',
        widgetId: 'panel-load-creation-sources',
        size: 'medium',
      },
      {
        instanceId: 'main-chart-revenue-by-month',
        widgetId: 'chart-revenue-by-month',
        size: 'medium',
      },
      { instanceId: 'main-chart-top-routes', widgetId: 'chart-top-routes', size: 'medium' },
      {
        instanceId: 'main-chart-revenue-by-customer',
        widgetId: 'chart-revenue-by-customer',
        size: 'medium',
      },
      { instanceId: 'main-chart-on-time-pct', widgetId: 'chart-on-time-pct', size: 'medium' },
      {
        instanceId: 'main-chart-load-status',
        widgetId: 'chart-load-status-distribution',
        size: 'medium',
      },
      { instanceId: 'main-chart-load-volume', widgetId: 'chart-load-volume', size: 'medium' },
      { instanceId: 'main-table-recent-loads', widgetId: 'table-recent-loads', size: 'large' },
    ],
  },
  'equipment-utilization': {
    id: 'equipment-utilization',
    name: 'Equipment Utilization',
    icon: 'truck',
    href: '/dashboard/equipment-utilization',
    builtIn: true,
    widgets: [
      { instanceId: 'equip-report', widgetId: 'report-equipment-utilization', size: 'large' },
    ],
  },
  'driver-activity': {
    id: 'driver-activity',
    name: 'Driver Activity',
    icon: 'users',
    href: '/dashboard/driver-activity',
    builtIn: true,
    widgets: [{ instanceId: 'driv-report', widgetId: 'report-driver-activity', size: 'large' }],
  },
  'ai-optimization': {
    id: 'ai-optimization',
    name: 'AI Optimization',
    icon: 'sparkles',
    href: '/dashboard/ai-optimization',
    builtIn: true,
    widgets: [{ instanceId: 'ai-report', widgetId: 'report-ai-optimization', size: 'large' }],
  },
  sla: {
    id: 'sla',
    name: 'SLA Dashboard',
    icon: 'shield',
    href: '/dashboard/sla',
    builtIn: true,
    widgets: [{ instanceId: 'sla-report', widgetId: 'report-sla', size: 'large' }],
  },
};

const BUILTIN_ORDER = [
  'main',
  'equipment-utilization',
  'driver-activity',
  'ai-optimization',
  'sla',
];

// Bump this when changing BUILTIN_DASHBOARDS defaults (sizes, instances, etc.)
// so a refresh applies the new defaults instead of restoring old per-user state.
// v4 — ATL-001470 Missing Documents widget becomes a full-width slim band
// (was v3's medium panel, which broke the chart-row pairing).
const STORAGE_VERSION = 'v4';

function storageKey(userId: string | undefined) {
  return `waypoint-dashboards-${STORAGE_VERSION}-${userId ?? 'anonymous'}`;
}

// Built-in widget instances added AFTER users may already have a saved
// layout. Bumping STORAGE_VERSION would wipe every user's customisations, so
// instead each id here is inserted into a saved layout exactly once (before
// the default instance that follows it); the ids already offered are
// remembered per user so a widget the user later removes stays removed.
const LATE_DEFAULT_INSTANCES: Record<string, string[]> = {
  // ATL-001472 — Loads Created This Week. ATL-001469 — NetSuite tiles.
  main: [
    'main-panel-load-creation-sources',
    'main-kpi-netsuite-pending-invoices',
    'main-kpi-netsuite-pending-settlements',
  ],
};

function seededDefaultsKey(userId: string | undefined) {
  return `waypoint-dashboards-seeded-${userId ?? 'anonymous'}`;
}

function readSeededDefaults(userId: string | undefined): Set<string> {
  try {
    const raw = localStorage.getItem(seededDefaultsKey(userId));
    const parsed: unknown = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed.filter((v) => typeof v === 'string') : []);
  } catch {
    return new Set();
  }
}

function writeSeededDefaults(userId: string | undefined, seeded: Set<string>) {
  try {
    localStorage.setItem(seededDefaultsKey(userId), JSON.stringify([...seeded]));
  } catch {
    // ignore
  }
}

/** Insert not-yet-offered late defaults into a saved built-in layout. */
export function applyLateDefaults(
  dashboardId: string,
  widgets: WidgetInstance[],
  seeded: Set<string>,
): WidgetInstance[] {
  const late = LATE_DEFAULT_INSTANCES[dashboardId];
  const defaults = BUILTIN_DASHBOARDS[dashboardId]?.widgets;
  if (!late || !defaults) return widgets;
  let next = widgets;
  for (const instanceId of late) {
    if (seeded.has(instanceId)) continue;
    seeded.add(instanceId);
    if (next.some((w) => w.instanceId === instanceId)) continue;
    const at = defaults.findIndex((w) => w.instanceId === instanceId);
    if (at < 0) continue;
    // Already on the layout under another instance (added from the catalog).
    if (next.some((w) => w.widgetId === defaults[at].widgetId)) continue;
    const instance = { ...defaults[at] };
    // Place it before the first default that follows it and is still on the
    // user's layout; append when none is.
    const successor = defaults
      .slice(at + 1)
      .map((d) => next.findIndex((w) => w.instanceId === d.instanceId))
      .find((i) => i >= 0);
    next =
      successor === undefined
        ? [...next, instance]
        : [...next.slice(0, successor), instance, ...next.slice(successor)];
  }
  return next;
}

function loadState(userId: string | undefined): PersistedState {
  const seed: PersistedState = {
    dashboards: structuredClone(BUILTIN_DASHBOARDS),
    order: [...BUILTIN_ORDER],
  };
  try {
    const raw = localStorage.getItem(storageKey(userId));
    const seeded = readSeededDefaults(userId);
    const seededBefore = seeded.size;
    if (!raw) {
      // Fresh layout already contains every default — mark them offered.
      for (const ids of Object.values(LATE_DEFAULT_INSTANCES)) ids.forEach((id) => seeded.add(id));
      writeSeededDefaults(userId, seeded);
      return seed;
    }
    const parsed = JSON.parse(raw) as Partial<PersistedState>;
    const dashboards: Record<string, Dashboard> = structuredClone(BUILTIN_DASHBOARDS);
    if (parsed.dashboards) {
      for (const [id, dash] of Object.entries(parsed.dashboards)) {
        if (!dash) continue;
        if (BUILTIN_DASHBOARDS[id]) {
          dashboards[id] = {
            ...BUILTIN_DASHBOARDS[id],
            widgets: Array.isArray(dash.widgets)
              ? applyLateDefaults(id, dash.widgets.map(normalizeWidget), seeded)
              : BUILTIN_DASHBOARDS[id].widgets,
          };
        } else {
          dashboards[id] = {
            id,
            name: dash.name ?? 'Untitled Dashboard',
            icon: dash.icon ?? 'layout-dashboard',
            href: dash.href ?? `/dashboard/custom/${id}`,
            builtIn: false,
            widgets: Array.isArray(dash.widgets) ? dash.widgets.map(normalizeWidget) : [],
          };
        }
      }
    }
    const order = Array.isArray(parsed.order)
      ? parsed.order.filter((id) => dashboards[id])
      : [...BUILTIN_ORDER];
    for (const id of BUILTIN_ORDER) if (!order.includes(id)) order.unshift(id);
    for (const id of Object.keys(dashboards)) if (!order.includes(id)) order.push(id);
    if (seeded.size !== seededBefore) {
      // Persist the layout with the inserted widgets BEFORE remembering they
      // were offered, so a failed save never hides them for good.
      saveState(userId, { dashboards, order });
      writeSeededDefaults(userId, seeded);
    }
    return { dashboards, order };
  } catch {
    return seed;
  }
}

function normalizeWidget(widget: WidgetInstance): WidgetInstance {
  const colors = Array.isArray(widget.chartColors)
    ? widget.chartColors.filter((color): color is string => typeof color === 'string')
    : undefined;
  return colors && colors.some(Boolean)
    ? { ...widget, chartColors: colors }
    : { ...widget, chartColors: undefined };
}

function applyWidgetChartColor(
  widgets: WidgetInstance[],
  instanceId: string,
  index: number,
  color: string
): WidgetInstance[] {
  if (!Number.isInteger(index) || index < 0) return widgets;
  const normalizedColor = color.trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(normalizedColor)) return widgets;
  return widgets.map((widget) => {
    if (widget.instanceId !== instanceId) return widget;
    const chartColors = [...(widget.chartColors ?? [])];
    chartColors[index] = normalizedColor;
    return { ...widget, chartColors };
  });
}

function clearWidgetChartColorAt(
  widgets: WidgetInstance[],
  instanceId: string,
  index: number
): WidgetInstance[] {
  if (!Number.isInteger(index) || index < 0) return widgets;
  return widgets.map((widget) => {
    if (widget.instanceId !== instanceId || !widget.chartColors?.length) return widget;
    const chartColors = [...widget.chartColors];
    delete chartColors[index];
    if (!chartColors.some(Boolean)) {
      const { chartColors: _chartColors, ...rest } = widget;
      return rest;
    }
    return { ...widget, chartColors };
  });
}

function saveState(userId: string | undefined, state: PersistedState) {
  try {
    localStorage.setItem(
      storageKey(userId),
      JSON.stringify({ dashboards: state.dashboards, order: state.order })
    );
  } catch {
    // ignore
  }
}

function slugify(name: string) {
  return name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '')
    .slice(0, 40);
}

export const useDashboardStore = create<DashboardStore>((set, get) => {
  const initial = loadState(undefined);
  return {
    userId: undefined,
    dashboards: initial.dashboards,
    order: initial.order,
    transientDashboards: {},

    initialize: (userId) => {
      if (get().userId === userId) return;
      const next = loadState(userId);
      set({ userId, dashboards: next.dashboards, order: next.order });
    },

    setTransientDashboard: (dashboard) => {
      set((state) => ({
        transientDashboards: { ...state.transientDashboards, [dashboard.id]: dashboard },
      }));
    },

    clearTransientDashboard: (id) => {
      set((state) => {
        if (!state.transientDashboards[id]) return state;
        const next = { ...state.transientDashboards };
        delete next[id];
        return { transientDashboards: next };
      });
    },

    getDashboard: (id) => get().dashboards[id] ?? get().transientDashboards[id],

    createDashboard: (name, opts) => {
      const base = slugify(name) || 'dashboard';
      const existing = get().dashboards;
      let id = base;
      let n = 1;
      while (existing[id]) {
        n += 1;
        id = `${base}-${n}`;
      }
      const dash: Dashboard = {
        id,
        name: name.trim() || 'Untitled Dashboard',
        icon: opts?.icon ?? 'layout-dashboard',
        href: `/dashboard/custom/${id}`,
        builtIn: false,
        widgets: [],
      };
      set((state) => {
        const next: PersistedState = {
          dashboards: { ...state.dashboards, [id]: dash },
          order: [...state.order, id],
        };
        saveState(state.userId, next);
        return next;
      });
      return id;
    },

    deleteDashboard: (id) => {
      set((state) => {
        const target = state.dashboards[id];
        if (!target || target.builtIn) return state;
        const dashboards = { ...state.dashboards };
        delete dashboards[id];
        const order = state.order.filter((d) => d !== id);
        const next: PersistedState = { dashboards, order };
        saveState(state.userId, next);
        return next;
      });
    },

    renameDashboard: (id, name) => {
      set((state) => {
        const target = state.dashboards[id];
        if (!target || target.builtIn) return state;
        const dashboards = {
          ...state.dashboards,
          [id]: { ...target, name: name.trim() || target.name },
        };
        const next: PersistedState = { dashboards, order: state.order };
        saveState(state.userId, next);
        return next;
      });
    },

    addWidget: (dashboardId, widgetId, size = 'medium') => {
      let createdId: string | null = null;
      set((state) => {
        const instanceId = `${dashboardId}-${widgetId}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 6)}`;
        if (state.dashboards[dashboardId]) {
          const dash = state.dashboards[dashboardId];
          createdId = instanceId;
          const widgets = [...dash.widgets, { instanceId, widgetId, size }];
          const dashboards = { ...state.dashboards, [dashboardId]: { ...dash, widgets } };
          saveState(state.userId, { dashboards, order: state.order });
          return { dashboards };
        }
        if (state.transientDashboards[dashboardId]) {
          const dash = state.transientDashboards[dashboardId];
          createdId = instanceId;
          const widgets = [...dash.widgets, { instanceId, widgetId, size }];
          return {
            transientDashboards: {
              ...state.transientDashboards,
              [dashboardId]: { ...dash, widgets },
            },
          };
        }
        return state;
      });
      return createdId;
    },

    removeWidget: (dashboardId, instanceId) => {
      set((state) => {
        if (state.dashboards[dashboardId]) {
          const dash = state.dashboards[dashboardId];
          const widgets = dash.widgets.filter((w) => w.instanceId !== instanceId);
          const dashboards = { ...state.dashboards, [dashboardId]: { ...dash, widgets } };
          saveState(state.userId, { dashboards, order: state.order });
          return { dashboards };
        }
        if (state.transientDashboards[dashboardId]) {
          const dash = state.transientDashboards[dashboardId];
          const widgets = dash.widgets.filter((w) => w.instanceId !== instanceId);
          return {
            transientDashboards: {
              ...state.transientDashboards,
              [dashboardId]: { ...dash, widgets },
            },
          };
        }
        return state;
      });
    },

    setWidgetSize: (dashboardId, instanceId, size) => {
      set((state) => {
        if (state.dashboards[dashboardId]) {
          const dash = state.dashboards[dashboardId];
          const widgets = dash.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, size } : w
          );
          const dashboards = { ...state.dashboards, [dashboardId]: { ...dash, widgets } };
          saveState(state.userId, { dashboards, order: state.order });
          return { dashboards };
        }
        if (state.transientDashboards[dashboardId]) {
          const dash = state.transientDashboards[dashboardId];
          const widgets = dash.widgets.map((w) =>
            w.instanceId === instanceId ? { ...w, size } : w
          );
          return {
            transientDashboards: {
              ...state.transientDashboards,
              [dashboardId]: { ...dash, widgets },
            },
          };
        }
        return state;
      });
    },

    setWidgetChartColor: (instanceId, index, color) => {
      set((state) => {
        let changed = false;
        const dashboards = { ...state.dashboards };
        for (const [dashboardId, dash] of Object.entries(state.dashboards)) {
          if (!dash.widgets.some((w) => w.instanceId === instanceId)) continue;
          changed = true;
          dashboards[dashboardId] = {
            ...dash,
            widgets: applyWidgetChartColor(dash.widgets, instanceId, index, color),
          };
        }
        const transientDashboards = { ...state.transientDashboards };
        for (const [dashboardId, dash] of Object.entries(state.transientDashboards)) {
          if (!dash.widgets.some((w) => w.instanceId === instanceId)) continue;
          changed = true;
          transientDashboards[dashboardId] = {
            ...dash,
            widgets: applyWidgetChartColor(dash.widgets, instanceId, index, color),
          };
        }
        if (!changed) return state;
        saveState(state.userId, { dashboards, order: state.order });
        return { dashboards, transientDashboards };
      });
    },

    clearWidgetChartColor: (instanceId, index) => {
      set((state) => {
        let changed = false;
        const dashboards = { ...state.dashboards };
        for (const [dashboardId, dash] of Object.entries(state.dashboards)) {
          if (!dash.widgets.some((w) => w.instanceId === instanceId)) continue;
          changed = true;
          dashboards[dashboardId] = {
            ...dash,
            widgets: clearWidgetChartColorAt(dash.widgets, instanceId, index),
          };
        }
        const transientDashboards = { ...state.transientDashboards };
        for (const [dashboardId, dash] of Object.entries(state.transientDashboards)) {
          if (!dash.widgets.some((w) => w.instanceId === instanceId)) continue;
          changed = true;
          transientDashboards[dashboardId] = {
            ...dash,
            widgets: clearWidgetChartColorAt(dash.widgets, instanceId, index),
          };
        }
        if (!changed) return state;
        saveState(state.userId, { dashboards, order: state.order });
        return { dashboards, transientDashboards };
      });
    },

    reorderWidgets: (dashboardId, instanceIds) => {
      set((state) => {
        const dash = state.dashboards[dashboardId] ?? state.transientDashboards[dashboardId];
        if (!dash) return state;
        const byId = new Map(dash.widgets.map((w) => [w.instanceId, w]));
        const widgets = instanceIds
          .map((id) => byId.get(id))
          .filter((w): w is WidgetInstance => Boolean(w));
        for (const w of dash.widgets) if (!instanceIds.includes(w.instanceId)) widgets.push(w);
        if (state.dashboards[dashboardId]) {
          const dashboards = { ...state.dashboards, [dashboardId]: { ...dash, widgets } };
          saveState(state.userId, { dashboards, order: state.order });
          return { dashboards };
        }
        return {
          transientDashboards: {
            ...state.transientDashboards,
            [dashboardId]: { ...dash, widgets },
          },
        };
      });
    },

    resetDashboard: (dashboardId) => {
      set((state) => {
        const builtIn = BUILTIN_DASHBOARDS[dashboardId];
        if (!builtIn) return state;
        const dashboards = { ...state.dashboards, [dashboardId]: structuredClone(builtIn) };
        const next: PersistedState = { dashboards, order: state.order };
        saveState(state.userId, next);
        return next;
      });
    },
  };
});

export function isBuiltInDashboard(id: string) {
  return Boolean(BUILTIN_DASHBOARDS[id]);
}

export function listDashboards(state: DashboardStore): Dashboard[] {
  return state.order.map((id) => state.dashboards[id]).filter(Boolean);
}
